<template>
  <div class="adjustment-wizard">
    <KTCodePreview :title="pageTitle">
      <template v-slot:toolbar>
        <b-button size="sm" variant="light" @click="$router.back()">
          <i class="fas fa-arrow-left mr-1"></i> Quay lại
        </b-button>
      </template>

      <template v-slot:preview>
        <!-- Step indicator -->
        <div class="step-indicator d-flex mb-4">
          <div
            v-for="s in steps"
            :key="s.num"
            class="step-item d-flex align-items-center"
            :class="{ active: step === s.num, done: step > s.num }"
          >
            <div class="step-circle">
              <i v-if="step > s.num" class="fas fa-check"></i>
              <span v-else>{{ s.num }}</span>
            </div>
            <span class="step-label ml-2">{{ s.label }}</span>
            <div v-if="s.num < steps.length" class="step-line mx-3"></div>
          </div>
        </div>

        <!-- ─── Step 1: Validate & Review ─── -->
        <div v-if="step === 1">
          <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary" /><br />
            <span class="text-muted mt-2 d-block">Đang kiểm tra tính hợp lệ...</span>
          </div>

          <div v-else-if="initError" class="alert alert-danger">
            <i class="fas fa-exclamation-circle mr-1"></i>{{ initError }}
          </div>

          <div v-else-if="validationResult">
            <div class="d-flex mb-3">
              <div class="summary-card mr-3 border-success">
                <div class="summary-number text-success">{{ validationResult.eligible }}</div>
                <div class="summary-label">Hợp lệ</div>
              </div>
              <div class="summary-card border-danger">
                <div class="summary-number text-danger">{{ validationResult.ineligible }}</div>
                <div class="summary-label">Không hợp lệ</div>
              </div>
            </div>

            <div v-if="validationResult.ineligible > 0" class="mb-3">
              <div class="section-title mb-2 text-danger"><strong>Hóa đơn không hợp lệ:</strong></div>
              <table class="table table-sm table-bordered">
                <thead class="thead-light">
                  <tr>
                    <th>Số hóa đơn trả</th>
                    <th>Lý do</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in ineligibleItems" :key="item.returnBillId">
                    <td>{{ item.returnBillNumber || item.returnBillId || '—' }}</td>
                    <td class="text-danger">{{ reasonCodeLabel(item.reasonCode) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="validationResult.eligible === 0" class="alert alert-warning">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              Không có hóa đơn nào hợp lệ để tạo điều chỉnh.
            </div>

            <div v-if="validationResult.eligible > 0" class="d-flex justify-content-end mt-3">
              <b-button variant="primary" @click="goToStep2">
                Tiếp theo <i class="fas fa-arrow-right ml-1"></i>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ─── Step 2: Configure per bill ─── -->
        <div v-if="step === 2">
          <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary" /><br />
            <span class="text-muted mt-2 d-block">Đang khởi tạo...</span>
          </div>

          <div v-else>
            <div class="alert alert-info py-2 mb-3">
              <i class="fas fa-info-circle mr-1"></i>
              Sau khi khởi tạo, hệ thống <strong>tự gọi xem trước lần lượt</strong> cho từng hóa đơn khi đủ dữ liệu bắt buộc. Hóa đơn thiếu trường cần điền thủ công rồi bấm
              <strong>Xem trước điều chỉnh</strong> (hoặc điền đủ để tự chạy lại). Tất cả phải xem trước thành công trước khi tạo nháp.
            </div>
            <div v-if="batchPreviewRunning" class="alert alert-secondary py-2 mb-2">
              <b-spinner small class="mr-2" />
              Đang xem trước tự động ({{ batchPreviewDone }}/{{ wizardItems.length }})…
            </div>

            <WizardBillSubform
              v-for="witem in wizardItems"
              :key="witem.returnBillId"
              ref="billSubforms"
              :item="witem"
              :wizardId="wizardId"
              @form-change="onFormChange"
            />

            <div class="d-flex justify-content-between mt-3">
              <b-button variant="light" @click="step = 1">
                <i class="fas fa-arrow-left mr-1"></i> Quay lại
              </b-button>
              <b-button
                variant="success"
                :disabled="!allPreviewed || isCreating"
                @click="onCreateDrafts"
              >
                <i v-if="isCreating" class="fas fa-spinner fa-spin mr-1"></i>
                <i v-else class="fas fa-file-alt mr-1"></i>
                Tạo nháp điều chỉnh ({{ wizardItems.length }})
              </b-button>
            </div>

            <div v-if="!allPreviewed" class="text-center text-muted mt-2" style="font-size:0.85rem">
              Còn {{ unpreviewed }} hóa đơn chưa được xem trước
            </div>
          </div>
        </div>

        <!-- ─── Step 3: Result ─── -->
        <div v-if="step === 3">
          <div v-if="isCreating" class="text-center py-5">
            <b-spinner variant="success" /><br />
            <span class="text-muted mt-2 d-block">Đang tạo nháp...</span>
          </div>

          <div v-else-if="createResult">
            <div class="text-center mb-4">
              <i class="fas fa-check-circle text-success" style="font-size:3rem"></i>
              <h5 class="mt-2">Hoàn tất</h5>
            </div>

            <div class="d-flex justify-content-center mb-4">
              <div class="summary-card mr-3 border-success">
                <div class="summary-number text-success">{{ createResult.created }}</div>
                <div class="summary-label">Nháp đã tạo</div>
              </div>
              <div v-if="createResult.failed > 0" class="summary-card border-danger">
                <div class="summary-number text-danger">{{ createResult.failed }}</div>
                <div class="summary-label">Lỗi</div>
              </div>
            </div>

            <div v-if="createResult.failed > 0" class="mb-3">
              <div class="section-title mb-2 text-danger"><strong>Tạo thất bại:</strong></div>
              <table class="table table-sm table-bordered">
                <thead class="thead-light">
                  <tr><th>Mã hóa đơn</th><th>Lỗi</th></tr>
                </thead>
                <tbody>
                  <tr v-for="item in failedCreateItems" :key="item.returnBillId">
                    <td>{{ item.returnBillId }}</td>
                    <td class="text-danger">{{ item.errorMessage }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-flex justify-content-center mt-3">
              <b-button variant="primary" @click="goToDraftList">
                <i class="fas fa-list mr-1"></i> Xem danh sách nháp điều chỉnh
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import WizardBillSubform from './components/WizardBillSubform.vue';

const REASON_CODE_LABELS = {
  INVALID_BILL_TYPE: 'Loại hóa đơn không phải trả hàng (type≠3)',
  RETURN_BILL_DELETED: 'Hóa đơn đã bị xóa',
  ORIGINAL_BILL_NOT_FOUND: 'Không tìm thấy hóa đơn gốc',
  UNSUPPORTED_BILL_TYPE: 'Loại hóa đơn gốc không được hỗ trợ',
  EINVOICE_NOT_ISSUED: 'Hóa đơn gốc chưa tạo hóa đơn điện tử',
  TECHNICAL_REFERENCE_MISSING: 'Hóa đơn bán gốc chưa được phát hành hoá đơn điện tử',
  ADJUSTMENT_DRAFT_ALREADY_EXISTS: 'Đã có nháp điều chỉnh đang hoạt động',
  ADJUSTMENT_ALREADY_ISSUED: 'Đã phát hành hóa đơn điều chỉnh',
  UNSUPPORTED_TAX_RATE: 'Tồn tại mặt hàng thuế suất 8% (POS không hỗ trợ)',
};

export default {
  name: 'AdjustmentWizard',
  components: { KTCodePreview, WizardBillSubform },
  data() {
    return {
      step: 1,
      isLoading: false,
      isCreating: false,
      initError: null,
      wizardId: null,
      validationResult: null,
      wizardItems: [],
      billForms: {},
      createResult: null,
      batchPreviewRunning: false,
      batchPreviewDone: 0,
      steps: [
        { num: 1, label: 'Kiểm tra hợp lệ' },
        { num: 2, label: 'Cấu hình' },
        { num: 3, label: 'Kết quả' },
      ],
    };
  },
  computed: {
    pageTitle() {
      return 'Tiến trình tạo hóa đơn điều chỉnh giảm GTGT';
    },
    returnBillIds() {
      const raw = this.$route.query.returnBillIds;
      const parts = Array.isArray(raw) ? raw : String(raw || '').split(',');
      return parts.filter(Boolean);
    },
    ineligibleItems() {
      if (!this.validationResult) return [];
      return (this.validationResult.items || []).filter((i) => !i.eligible);
    },
    eligibleItems() {
      if (!this.validationResult) return [];
      return (this.validationResult.items || []).filter((i) => i.eligible);
    },
    allPreviewed() {
      return this.wizardItems.length > 0 && this.wizardItems.every((wi) => {
        const form = this.billForms[String(wi.returnBillId)];
        return form && form.previewed;
      });
    },
    unpreviewed() {
      return this.wizardItems.filter((wi) => {
        const form = this.billForms[String(wi.returnBillId)];
        return !form || !form.previewed;
      }).length;
    },
    failedCreateItems() {
      if (!this.createResult) return [];
      return (this.createResult.items || []).filter((i) => i.status === 'FAILED');
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hóa đơn điều chỉnh GTGT', route: 'adjustment-draft-list' },
      { title: 'Tiến trình tạo điều chỉnh' },
    ]);
    this.runValidation();
  },
  methods: {
    reasonCodeLabel(code) {
      return REASON_CODE_LABELS[code] || code;
    },
    onFormChange(formData) {
      this.$set(this.billForms, String(formData.returnBillId), formData);
    },
    async runValidation() {
      if (!this.returnBillIds.length) {
        this.initError = 'Không có hóa đơn trả nào được chọn. Vui lòng quay lại và chọn hóa đơn.';
        return;
      }
      this.isLoading = true;
      this.initError = null;
      try {
        const { data: vRes } = await ApiService.post('v1/invoice-adjustment-wizard/validate-selection', {
          returnBillIds: this.returnBillIds,
        });
        this.validationResult = vRes.data || vRes;
      } catch (err) {
        this.initError = err?.response?.data?.message || 'Lỗi kiểm tra hợp lệ.';
      } finally {
        this.isLoading = false;
      }
    },
    async goToStep2() {
      this.isLoading = true;
      try {
        const eligibleIds = this.eligibleItems.map((i) => i.returnBillId);
        const { data: initRes } = await ApiService.post('v1/invoice-adjustment-wizard/init', {
          returnBillIds: eligibleIds,
        });
        const result = initRes.data || initRes;
        this.wizardId = result.wizardId;
        this.wizardItems = result.items || [];
        this.billForms = {};
        this.step = 2;
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Lỗi khởi tạo wizard.');
      } finally {
        this.isLoading = false;
      }
      if (this.step === 2 && this.wizardItems.length && this.wizardId != null) {
        await this.$nextTick();
        await this.runBillSubformPreviews();
      }
    },
    async runBillSubformPreviews() {
      const raw = this.$refs.billSubforms;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      if (!list.length) return;
      this.batchPreviewRunning = true;
      this.batchPreviewDone = 0;
      try {
        for (let i = 0; i < list.length; i++) {
          const comp = list[i];
          if (comp && typeof comp.requestPreviewIfReady === 'function') {
            await comp.requestPreviewIfReady();
          }
          this.batchPreviewDone = i + 1;
        }
      } finally {
        this.batchPreviewRunning = false;
      }
    },
    async onCreateDrafts() {
      if (!this.allPreviewed) return;
      this.isCreating = true;
      this.step = 3;
      try {
        const items = Object.values(this.billForms).map((f) => ({
          returnBillId: f.returnBillId,
          buyerInfo: f.buyerInfo,
          displayReference: f.displayReference,
          reason: f.reason,
          create: true,
        }));
        const { data: res } = await ApiService.post(`v1/invoice-adjustment-wizard/${this.wizardId}/create-drafts`, {
          items,
        });
        this.createResult = res.data || res;
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Lỗi tạo nháp điều chỉnh.');
        this.step = 2;
      } finally {
        this.isCreating = false;
      }
    },
    goToDraftList() {
      this.$router.push({ name: 'adjustment-draft-list' });
    },
  },
};
</script>

<style lang="scss" scoped>
.step-indicator {
  .step-item {
    &.active .step-circle { background: #3699ff; color: #fff; border-color: #3699ff; }
    &.done .step-circle { background: #1bc5bd; color: #fff; border-color: #1bc5bd; }
  }
  .step-circle {
    width: 28px; height: 28px;
    border: 2px solid #d1d5db;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.8rem; font-weight: 600;
    color: #6b7280;
    flex-shrink: 0;
  }
  .step-label { font-size: 0.85rem; color: #374151; white-space: nowrap; }
  .step-line { flex: 1; min-width: 30px; height: 2px; background: #e5e7eb; }
}

.summary-card {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 24px;
  text-align: center;
  .summary-number { font-size: 2rem; font-weight: 700; }
  .summary-label { font-size: 0.85rem; color: #6b7280; }
}

.section-title { font-size: 0.9rem; }
</style>
